import React, { useState, useEffect } from 'react';
import './TeamBuilderPage.css';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import { FUNCTIONS_URL } from '../services/supabaseConfig';

interface BuildFromDB {
  id: string;
  owner_id: string;
  name: string;
  description: string;
  hitter_slots: any[];
  pitcher_slots: any[];
  batting_order: string[];
  rotation_order: string[];
  depth_chart: Record<string, string[]>;
  strategy: Record<string, number>;
  avg_ovr: number;
  max_ovr: number;
  is_active: boolean;
  active_league_id: string | null;
  created_at: string;
  updated_at: string;
}

interface PlayerFromDB {
  id: string;
  first_name: string;
  last_name: string;
  position: string;
  overall: number;
  is_hitter: boolean;
  power: number;
  contact: number;
  speed: number;
  velocity: number;
  control: number;
  movement: number;
  user_id: string;
  league_id: string;
  team_id: string;
  retired: boolean;
  is_deceased: boolean;
  ghosted_in_league: string | null;
}

interface TeamBuilderPageProps {
  onNavigate?: (page: string) => void;
  userData?: any;
}

const VALID_HITTER_POSITIONS = ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF'];
const REQUIRED_HITTERS = 14;
const REQUIRED_PITCHERS = 11;

const TeamBuilderPage: React.FC<TeamBuilderPageProps> = ({ onNavigate, userData }) => {
  const userId = localStorage.getItem('userId') || '';
  const [builds, setBuilds] = useState<BuildFromDB[]>([]);
  const [selectedBuild, setSelectedBuild] = useState<BuildFromDB | null>(null);
  const [userPlayers, setUserPlayers] = useState<PlayerFromDB[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'builds' | 'editor'>('builds');
  const [editTab, setEditTab] = useState<'roster' | 'lineup' | 'rotation' | 'depth' | 'strategy'>('roster');
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  
  // New build dialog
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newBuildName, setNewBuildName] = useState('');
  const [newBuildDesc, setNewBuildDesc] = useState('');
  
  // Strategy sliders
  const strategyDefaults = {
    stealAttempts: 50,
    sacrificeBunts: 50,
    shiftFrequency: 50,
    hittingApproach: 50,
    intentionalWalks: 50,
    pitcherManagement: 50,
    baseRunningAggression: 50,
  };

  useEffect(() => {
    loadBuilds();
    loadUserPlayers();
  }, []);

  async function loadBuilds() {
    if (!isSupabaseConfigured() || !userId) return;
    const { data } = await supabase
      .from('team_builds')
      .select('*')
      .eq('owner_id', userId)
      .order('updated_at', { ascending: false });
    setBuilds(data || []);
    setLoading(false);
  }

  async function loadUserPlayers() {
    if (!isSupabaseConfigured() || !userId) return;
    // Get all players owned by this user that are active (not retired, not deceased)
    // and are on a traditional league team
    const { data: tradLeagues } = await supabase
      .from('leagues')
      .select('id')
      .eq('league_type', 'traditional');
    
    const tradIds = (tradLeagues || []).map(l => l.id);
    if (tradIds.length === 0) { setUserPlayers([]); return; }

    const { data } = await supabase
      .from('players')
      .select('*')
      .eq('user_id', userId)
      .in('league_id', tradIds)
      .eq('retired', false)
      .eq('is_deceased', false)
      .order('overall', { ascending: false });
    
    setUserPlayers(data || []);
  }

  async function createBuild() {
    if (!newBuildName.trim()) return;
    const resp = await fetch(`${FUNCTIONS_URL}/team-builds`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-admin-secret': 'simforge-admin-2024' },
      body: JSON.stringify({ user_id: userId, action: 'create', name: newBuildName.trim(), description: newBuildDesc.trim() }),
    });
    const result = await resp.json();
    if (result.build) {
      await loadBuilds();
      selectBuild(result.build);
    } else {
      alert(result.error || 'Failed to create build');
    }
    setShowNewDialog(false);
    setNewBuildName('');
    setNewBuildDesc('');
  }

  async function deleteBuild(buildId: string) {
    if (!confirm('Delete this build? This cannot be undone.')) return;
    const resp = await fetch(`${FUNCTIONS_URL}/team-builds`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-admin-secret': 'simforge-admin-2024' },
      body: JSON.stringify({ user_id: userId, action: 'delete', build_id: buildId }),
    });
    const result = await resp.json();
    if (result.success) {
      setSelectedBuild(null);
      setActiveTab('builds');
      await loadBuilds();
    } else {
      alert(result.error || 'Failed to delete');
    }
  }

  async function cloneBuild(buildId: string) {
    const name = prompt('Name for cloned build:');
    if (!name) return;
    const resp = await fetch(`${FUNCTIONS_URL}/team-builds`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-admin-secret': 'simforge-admin-2024' },
      body: JSON.stringify({ user_id: userId, action: 'clone', build_id: buildId, name }),
    });
    const result = await resp.json();
    if (result.build) {
      await loadBuilds();
    } else {
      alert(result.error || 'Failed to clone');
    }
  }

  async function saveBuild() {
    if (!selectedBuild) return;
    setSaving(true);
    const resp = await fetch(`${FUNCTIONS_URL}/team-builds`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-admin-secret': 'simforge-admin-2024' },
      body: JSON.stringify({
        user_id: userId,
        action: 'update',
        build_id: selectedBuild.id,
        hitter_slots: selectedBuild.hitter_slots,
        pitcher_slots: selectedBuild.pitcher_slots,
        batting_order: selectedBuild.batting_order,
        rotation_order: selectedBuild.rotation_order,
        depth_chart: selectedBuild.depth_chart,
        strategy: selectedBuild.strategy,
      }),
    });
    const result = await resp.json();
    if (result.build) {
      setSelectedBuild(result.build);
      setValidationErrors([]);
    } else {
      alert(result.error || 'Failed to save');
    }
    setSaving(false);
  }

  async function validateBuild() {
    if (!selectedBuild) return;
    const resp = await fetch(`${FUNCTIONS_URL}/team-builds`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-admin-secret': 'simforge-admin-2024' },
      body: JSON.stringify({ user_id: userId, action: 'validate', build_id: selectedBuild.id }),
    });
    const result = await resp.json();
    setValidationErrors(result.errors || []);
    return result.valid;
  }

  function selectBuild(build: BuildFromDB) {
    setSelectedBuild(build);
    setActiveTab('editor');
    setEditTab('roster');
    setValidationErrors([]);
  }

  // Roster management
  function addHitter(playerId: string) {
    if (!selectedBuild) return;
    const player = userPlayers.find(p => p.id === playerId);
    if (!player) return;
    const slots = [...(selectedBuild.hitter_slots || [])];
    if (slots.length >= REQUIRED_HITTERS) { alert(`Max ${REQUIRED_HITTERS} hitters`); return; }
    if (slots.some((s: any) => (s.id || s[0] || s) === playerId)) { alert('Player already in build'); return; }
    slots.push({ id: playerId, position: player.position, overall: player.overall });
    setSelectedBuild({ ...selectedBuild, hitter_slots: slots });
  }

  function addPitcher(playerId: string) {
    if (!selectedBuild) return;
    const player = userPlayers.find(p => p.id === playerId);
    if (!player) return;
    const slots = [...(selectedBuild.pitcher_slots || [])];
    if (slots.length >= REQUIRED_PITCHERS) { alert(`Max ${REQUIRED_PITCHERS} pitchers`); return; }
    if (slots.includes(playerId)) { alert('Player already in build'); return; }
    slots.push(playerId);
    setSelectedBuild({ ...selectedBuild, pitcher_slots: slots });
  }

  function removeHitter(idx: number) {
    if (!selectedBuild) return;
    const slots = [...selectedBuild.hitter_slots];
    const removed = slots.splice(idx, 1)[0];
    // Also remove from batting order and depth chart
    const playerId = typeof removed === 'string' ? removed : removed.id || removed[0];
    const batting = (selectedBuild.batting_order || []).filter(id => id !== playerId);
    const depth: Record<string, string[]> = { ...selectedBuild.depth_chart };
    for (const pos of Object.keys(depth)) {
      depth[pos] = (depth[pos] || []).filter(id => id !== playerId);
    }
    setSelectedBuild({ ...selectedBuild, hitter_slots: slots, batting_order: batting, depth_chart: depth });
  }

  function removePitcher(idx: number) {
    if (!selectedBuild) return;
    const slots = [...selectedBuild.pitcher_slots];
    const removed = slots.splice(idx, 1)[0];
    const rotation = (selectedBuild.rotation_order || []).filter(id => id !== removed);
    setSelectedBuild({ ...selectedBuild, pitcher_slots: slots, rotation_order: rotation });
  }

  // Batting order
  function moveInOrder(idx: number, dir: -1 | 1) {
    if (!selectedBuild) return;
    const order = [...selectedBuild.batting_order];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= order.length) return;
    [order[idx], order[newIdx]] = [order[newIdx], order[idx]];
    setSelectedBuild({ ...selectedBuild, batting_order: order });
  }

  function addToLineup(playerId: string) {
    if (!selectedBuild) return;
    const order = [...selectedBuild.batting_order];
    if (order.includes(playerId)) return;
    if (order.length >= 9) { alert('Lineup has 9 slots'); return; }
    order.push(playerId);
    setSelectedBuild({ ...selectedBuild, batting_order: order });
  }

  function removeFromLineup(idx: number) {
    if (!selectedBuild) return;
    const order = [...selectedBuild.batting_order];
    order.splice(idx, 1);
    setSelectedBuild({ ...selectedBuild, batting_order: order });
  }

  // Rotation order
  function moveInRotation(idx: number, dir: -1 | 1) {
    if (!selectedBuild) return;
    const order = [...selectedBuild.rotation_order];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= order.length) return;
    [order[idx], order[newIdx]] = [order[newIdx], order[idx]];
    setSelectedBuild({ ...selectedBuild, rotation_order: order });
  }

  function addToRotation(playerId: string) {
    if (!selectedBuild) return;
    const order = [...selectedBuild.rotation_order];
    if (order.includes(playerId)) return;
    order.push(playerId);
    setSelectedBuild({ ...selectedBuild, rotation_order: order });
  }

  function removeFromRotation(idx: number) {
    if (!selectedBuild) return;
    const order = [...selectedBuild.rotation_order];
    order.splice(idx, 1);
    setSelectedBuild({ ...selectedBuild, rotation_order: order });
  }

  // Depth chart
  function setDepthPosition(position: string, playerId: string) {
    if (!selectedBuild) return;
    const depth = { ...selectedBuild.depth_chart };
    const current = depth[position] || [];
    if (current.includes(playerId)) return;
    // Remove from any other position first
    for (const pos of Object.keys(depth)) {
      depth[pos] = (depth[pos] || []).filter(id => id !== playerId);
    }
    depth[position] = [...current, playerId];
    setSelectedBuild({ ...selectedBuild, depth_chart: depth });
  }

  function removeFromDepth(position: string, playerId: string) {
    if (!selectedBuild) return;
    const depth = { ...selectedBuild.depth_chart };
    depth[position] = (depth[position] || []).filter(id => id !== playerId);
    setSelectedBuild({ ...selectedBuild, depth_chart: depth });
  }

  // Strategy
  function setStrategy(key: string, value: number) {
    if (!selectedBuild) return;
    const strategy = { ...(selectedBuild.strategy || strategyDefaults) };
    strategy[key] = Math.max(0, Math.min(100, value));
    setSelectedBuild({ ...selectedBuild, strategy });
  }

  // Get player display info
  function getPlayer(id: string): PlayerFromDB | undefined {
    return userPlayers.find(p => p.id === id);
  }

  function getHitterId(slot: any): string {
    return typeof slot === 'string' ? slot : slot.id || slot[0] || '';
  }

  // Count available vs needed
  const availableHitters = userPlayers.filter(p => p.is_hitter).length;
  const availablePitchers = userPlayers.filter(p => !p.is_hitter).length;

  if (loading) return <div className="team-builder-page"><p>Loading...</p></div>;

  // ====== BUILDS LIST VIEW ======
  if (activeTab === 'builds') {
    return (
      <div className="team-builder-page">
        <div className="tb-header">
          <div>
            <h1>🏗️ Team Builder</h1>
            <p className="subtitle">Pre-build your Quick League rosters from your owned players</p>
          </div>
          <button className="btn-primary" onClick={() => setShowNewDialog(true)}>
            + New Build
          </button>
        </div>

        <div className="tb-player-summary">
          <span>📊 Your Stable: <strong>{userPlayers.length}</strong> players ({availableHitters} hitters, {availablePitchers} pitchers)</span>
          <span className="tb-requirement">Need {REQUIRED_HITTERS}H + {REQUIRED_PITCHERS}P per build</span>
        </div>

        {builds.length === 0 ? (
          <div className="tb-empty">
            <p>No builds yet. Create your first team build to enter Owned-Player Quick Leagues!</p>
            <button className="btn-primary" onClick={() => setShowNewDialog(true)}>Create First Build</button>
          </div>
        ) : (
          <div className="tb-grid">
            {builds.map(build => (
              <div key={build.id} className={`tb-card ${build.is_active ? 'active' : ''}`}>
                <div className="tb-card-header">
                  <h3>{build.name}</h3>
                  {build.is_active && <span className="tb-badge active">⚡ In League</span>}
                </div>
                {build.description && <p className="tb-desc">{build.description}</p>}
                <div className="tb-card-stats">
                  <div className="tb-stat">
                    <span className="label">Hitters</span>
                    <span className="value">{(build.hitter_slots || []).length}/{REQUIRED_HITTERS}</span>
                  </div>
                  <div className="tb-stat">
                    <span className="label">Pitchers</span>
                    <span className="value">{(build.pitcher_slots || []).length}/{REQUIRED_PITCHERS}</span>
                  </div>
                  <div className="tb-stat">
                    <span className="label">Avg OVR</span>
                    <span className="value">{build.avg_ovr ? build.avg_ovr.toFixed(1) : '—'}</span>
                  </div>
                  <div className="tb-stat">
                    <span className="label">Max OVR</span>
                    <span className="value">{build.max_ovr || '—'}</span>
                  </div>
                </div>
                <div className="tb-card-actions">
                  <button className="btn-small primary" onClick={() => selectBuild(build)}>✏️ Edit</button>
                  <button className="btn-small" onClick={() => cloneBuild(build.id)}>📋 Clone</button>
                  <button className="btn-small danger" onClick={() => deleteBuild(build.id)} disabled={build.is_active}>🗑️</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* New Build Dialog */}
        {showNewDialog && (
          <div className="tb-dialog-overlay" onClick={() => setShowNewDialog(false)}>
            <div className="tb-dialog" onClick={e => e.stopPropagation()}>
              <h3>Create New Build</h3>
              <input
                type="text"
                placeholder="Build name (e.g. Speed Demons)"
                value={newBuildName}
                onChange={e => setNewBuildName(e.target.value)}
                autoFocus
              />
              <input
                type="text"
                placeholder="Description (optional)"
                value={newBuildDesc}
                onChange={e => setNewBuildDesc(e.target.value)}
              />
              <div className="tb-dialog-actions">
                <button className="btn-primary" onClick={createBuild}>Create</button>
                <button className="btn-secondary" onClick={() => setShowNewDialog(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ====== EDITOR VIEW ======
  if (!selectedBuild) return null;

  const hitterCount = (selectedBuild.hitter_slots || []).length;
  const pitcherCount = (selectedBuild.pitcher_slots || []).length;
  const isComplete = hitterCount >= REQUIRED_HITTERS && pitcherCount >= REQUIRED_PITCHERS &&
    (selectedBuild.batting_order || []).length >= 9 &&
    (selectedBuild.rotation_order || []).length >= 5;

  return (
    <div className="team-builder-page editor">
      <div className="tb-editor-header">
        <button className="btn-back" onClick={() => { setActiveTab('builds'); setSelectedBuild(null); }}>← Back to Builds</button>
        <div>
          <h2>{selectedBuild.name}</h2>
          <span className="tb-complete-badge">{isComplete ? '✅ Ready' : '⚠️ Incomplete'}</span>
        </div>
        <div className="tb-editor-actions">
          <button className="btn-secondary" onClick={validateBuild}>🔍 Validate</button>
          <button className="btn-primary" onClick={saveBuild} disabled={saving}>
            {saving ? 'Saving...' : '💾 Save'}
          </button>
        </div>
      </div>

      {/* Validation errors */}
      {validationErrors.length > 0 && (
        <div className="tb-validation-errors">
          {validationErrors.map((err, i) => <div key={i} className="tb-error">❌ {err}</div>)}
        </div>
      )}

      {/* Tabs */}
      <div className="tb-edit-tabs">
        {(['roster', 'lineup', 'rotation', 'depth', 'strategy'] as const).map(tab => (
          <button
            key={tab}
            className={`tb-edit-tab ${editTab === tab ? 'active' : ''}`}
            onClick={() => setEditTab(tab)}
          >
            {tab === 'roster' ? '👥 Roster' :
             tab === 'lineup' ? '⚾ Lineup' :
             tab === 'rotation' ? '🔄 Rotation' :
             tab === 'depth' ? '📊 Depth Chart' :
             '🎯 Strategy'}
          </button>
        ))}
      </div>

      {/* ROSTER TAB */}
      {editTab === 'roster' && (
        <div className="tb-roster">
          <div className="tb-roster-columns">
            {/* Available Players */}
            <div className="tb-available">
              <h3>Your Players ({userPlayers.length})</h3>
              <div className="tb-player-list">
                {userPlayers.map(p => {
                  const inBuild = selectedBuild.hitter_slots?.some((s: any) => getHitterId(s) === p.id) ||
                    selectedBuild.pitcher_slots?.includes(p.id);
                  return (
                    <div key={p.id} className={`tb-player-card ${inBuild ? 'in-build' : ''}`}>
                      <span className="tb-pos">{p.position}</span>
                      <span className="tb-name">{p.first_name} {p.last_name.charAt(0)}.</span>
                      <span className="tb-ovr">{p.overall}</span>
                      {!inBuild && (
                        <button
                          className="tb-add-btn"
                          onClick={() => p.is_hitter ? addHitter(p.id) : addPitcher(p.id)}
                          disabled={p.is_hitter ? hitterCount >= REQUIRED_HITTERS : pitcherCount >= REQUIRED_PITCHERS}
                        >
                          +
                        </button>
                      )}
                      {inBuild && <span className="tb-check">✓</span>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Build Roster */}
            <div className="tb-build-roster">
              <div className="tb-roster-section">
                <h3>Hitters ({hitterCount}/{REQUIRED_HITTERS})</h3>
                {selectedBuild.hitter_slots?.map((slot: any, idx: number) => {
                  const pid = getHitterId(slot);
                  const p = getPlayer(pid);
                  return (
                    <div key={idx} className="tb-slot-card">
                      <span className="tb-slot-num">H{idx + 1}</span>
                      <span className="tb-pos">{p?.position || '—'}</span>
                      <span className="tb-name">{p ? `${p.first_name} ${p.last_name.charAt(0)}.` : 'Empty'}</span>
                      <span className="tb-ovr">{p?.overall || '—'}</span>
                      <button className="tb-remove-btn" onClick={() => removeHitter(idx)}>✕</button>
                    </div>
                  );
                })}
              </div>
              <div className="tb-roster-section">
                <h3>Pitchers ({pitcherCount}/{REQUIRED_PITCHERS})</h3>
                {selectedBuild.pitcher_slots?.map((pid: string, idx: number) => {
                  const p = getPlayer(pid);
                  return (
                    <div key={idx} className="tb-slot-card">
                      <span className="tb-slot-num">P{idx + 1}</span>
                      <span className="tb-pos">{p?.position || 'P'}</span>
                      <span className="tb-name">{p ? `${p.first_name} ${p.last_name.charAt(0)}.` : 'Empty'}</span>
                      <span className="tb-ovr">{p?.overall || '—'}</span>
                      <button className="tb-remove-btn" onClick={() => removePitcher(idx)}>✕</button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* LINEUP TAB */}
      {editTab === 'lineup' && (
        <div className="tb-lineup">
          <div className="tb-lineup-columns">
            <div className="tb-lineup-order">
              <h3>Batting Order ({selectedBuild.batting_order?.length || 0}/9)</h3>
              {[0, 1, 2, 3, 4, 5, 6, 7, 8].map(idx => {
                const pid = selectedBuild.batting_order?.[idx];
                const p = pid ? getPlayer(pid) : null;
                return (
                  <div key={idx} className={`tb-lineup-slot ${p ? 'filled' : 'empty'}`}>
                    <span className="tb-lineup-num">{idx + 1}</span>
                    {p ? (
                      <>
                        <span className="tb-pos">{p.position}</span>
                        <span className="tb-name">{p.first_name} {p.last_name.charAt(0)}.</span>
                        <span className="tb-ovr">{p.overall}</span>
                        <div className="tb-lineup-arrows">
                          {idx > 0 && <button onClick={() => moveInOrder(idx, -1)}>▲</button>}
                          {idx < (selectedBuild.batting_order?.length || 0) - 1 && <button onClick={() => moveInOrder(idx, 1)}>▼</button>}
                        </div>
                        <button className="tb-remove-btn" onClick={() => removeFromLineup(idx)}>✕</button>
                      </>
                    ) : (
                      <span className="tb-empty-slot">— Empty —</span>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="tb-lineup-pool">
              <h3>Available Hitters</h3>
              {selectedBuild.hitter_slots?.map((slot: any) => {
                const pid = getHitterId(slot);
                const p = getPlayer(pid);
                const inLineup = selectedBuild.batting_order?.includes(pid);
                return p && !inLineup ? (
                  <div key={pid} className="tb-player-card">
                    <span className="tb-pos">{p.position}</span>
                    <span className="tb-name">{p.first_name} {p.last_name.charAt(0)}.</span>
                    <span className="tb-ovr">{p.overall}</span>
                    <button className="tb-add-btn" onClick={() => addToLineup(pid)}>+</button>
                  </div>
                ) : null;
              })}
            </div>
          </div>
        </div>
      )}

      {/* ROTATION TAB */}
      {editTab === 'rotation' && (
        <div className="tb-rotation">
          <div className="tb-lineup-columns">
            <div className="tb-lineup-order">
              <h3>Rotation Order ({selectedBuild.rotation_order?.length || 0}/5+ pitchers)</h3>
              {(selectedBuild.rotation_order || []).map((pid: string, idx: number) => {
                const p = getPlayer(pid);
                return (
                  <div key={idx} className="tb-lineup-slot filled">
                    <span className="tb-lineup-num">SP{idx + 1}</span>
                    <span className="tb-pos">{p?.position || 'P'}</span>
                    <span className="tb-name">{p ? `${p.first_name} ${p.last_name.charAt(0)}.` : 'Unknown'}</span>
                    <span className="tb-ovr">{p?.overall || '—'}</span>
                    <div className="tb-lineup-arrows">
                      {idx > 0 && <button onClick={() => moveInRotation(idx, -1)}>▲</button>}
                      {idx < (selectedBuild.rotation_order?.length || 0) - 1 && <button onClick={() => moveInRotation(idx, 1)}>▼</button>}
                    </div>
                    <button className="tb-remove-btn" onClick={() => removeFromRotation(idx)}>✕</button>
                  </div>
                );
              })}
            </div>
            <div className="tb-lineup-pool">
              <h3>Available Pitchers</h3>
              {selectedBuild.pitcher_slots?.map((pid: string) => {
                const p = getPlayer(pid);
                const inRotation = selectedBuild.rotation_order?.includes(pid);
                return p && !inRotation ? (
                  <div key={pid} className="tb-player-card">
                    <span className="tb-pos">{p.position}</span>
                    <span className="tb-name">{p.first_name} {p.last_name.charAt(0)}.</span>
                    <span className="tb-ovr">{p.overall}</span>
                    <button className="tb-add-btn" onClick={() => addToRotation(pid)}>+</button>
                  </div>
                ) : null;
              })}
            </div>
          </div>
        </div>
      )}

      {/* DEPTH CHART TAB */}
      {editTab === 'depth' && (
        <div className="tb-depth">
          {[...VALID_HITTER_POSITIONS, 'DH'].map(pos => {
            const depthList = selectedBuild.depth_chart?.[pos] || [];
            return (
              <div key={pos} className="tb-depth-row">
                <span className="tb-depth-pos">{pos}</span>
                <div className="tb-depth-slots">
                  {depthList.map((pid: string, idx: number) => {
                    const p = getPlayer(pid);
                    return (
                      <span key={idx} className="tb-depth-player">
                        {p ? `${p.first_name} ${p.last_name.charAt(0)}. (${p.overall})` : '—'}
                        <button className="tb-remove-btn tiny" onClick={() => removeFromDepth(pos, pid)}>✕</button>
                      </span>
                    );
                  })}
                  {depthList.length === 0 && <span className="tb-empty-slot">—</span>}
                </div>
                <select
                  className="tb-depth-select"
                  onChange={e => { if (e.target.value) setDepthPosition(pos, e.target.value); }}
                  value=""
                >
                  <option value="">+ Add</option>
                  {selectedBuild.hitter_slots?.map((slot: any) => {
                    const pid = getHitterId(slot);
                    const p = getPlayer(pid);
                    const alreadyUsed = Object.values(selectedBuild.depth_chart || {}).some(
                      (ids: any) => Array.isArray(ids) && ids.includes(pid)
                    );
                    return p && !alreadyUsed ? (
                      <option key={pid} value={pid}>{p.first_name} {p.last_name.charAt(0)}. ({p.overall})</option>
                    ) : null;
                  })}
                </select>
              </div>
            );
          })}
        </div>
      )}

      {/* STRATEGY TAB */}
      {editTab === 'strategy' && (
        <div className="tb-strategy">
          {Object.entries(selectedBuild.strategy || strategyDefaults).map(([key, value]) => (
            <div key={key} className="tb-strategy-row">
              <label>{key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</label>
              <div className="tb-slider-group">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={value}
                  onChange={e => setStrategy(key, parseInt(e.target.value))}
                />
                <span className="tb-slider-val">{value}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamBuilderPage;
